import { pickColor, pickExplosionThreshold } from './data';
import { timing, variantForLevel, } from './params';
export function makeStimulus(trialIdx, maxPumpsValue, level) {
    const variant = variantForLevel(level);
    const clampedMax = Math.max(8, Math.min(64, Math.round(maxPumpsValue)));
    const explosionThresholdHidden = pickExplosionThreshold(clampedMax, trialIdx + 1);
    const targetPumps = Math.max(1, Math.round(clampedMax * 0.5));
    return {
        trialIdx,
        maxPumps: clampedMax,
        rewardPerPump: variant.rewardPerPump,
        targetPumps,
        explosionThresholdHidden,
        color: pickColor(trialIdx),
        showLiveCounter: variant.showLiveCounter,
    };
}
export function createBartAdapter(level = 1) {
    let state = {
        subPhase: 'idle',
        stim: null,
        feedback: null,
        level,
        variant: variantForLevel(level),
        pumps: 0,
        provisional: 0,
        bankTotal: 0,
        lastOutcome: null,
    };
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let trialStart = 0;
    const timers = [];
    function clearTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    function settle(stim, outcome) {
        if (!resolver)
            return;
        const banked = outcome === 'banked' ? state.provisional : 0;
        const correct = outcome === 'banked' && state.pumps >= stim.targetPumps;
        update({
            lastOutcome: outcome,
            bankTotal: state.bankTotal + banked,
        });
        const r = resolver;
        resolver = null;
        rejecter = null;
        clearTimers();
        r({
            outcome,
            pumps: state.pumps,
            banked,
            reactionTimeMs: Date.now() - trialStart,
            correct,
        });
    }
    return {
        mount() {
            // Idempotent.
        },
        unmount() {
            clearTimers();
            listeners.clear();
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(stim) {
            clearTimers();
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                update({
                    subPhase: 'prepare',
                    stim,
                    feedback: null,
                    pumps: 0,
                    provisional: 0,
                });
                timers.push(setTimeout(() => {
                    update({ subPhase: 'inflate' });
                    trialStart = Date.now();
                }, timing.prepareMs));
            });
        },
        async showFeedback(correct) {
            update({
                subPhase: 'feedback',
                feedback: correct ? 'correct' : 'incorrect',
            });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, timing.feedbackMs));
            });
        },
        pause() {
            // Sprint 0.5 : no-op.
        },
        resume() {
            // Sprint 0.5 : no-op.
        },
        abort() {
            clearTimers();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        pump() {
            if (state.subPhase !== 'inflate' || state.stim === null)
                return;
            const nextPumps = state.pumps + 1;
            const nextProv = state.provisional + state.stim.rewardPerPump;
            update({ pumps: nextPumps, provisional: nextProv });
            if (nextPumps >= state.stim.explosionThresholdHidden) {
                settle(state.stim, 'popped');
            }
        },
        bank() {
            if (state.subPhase !== 'inflate' || state.stim === null)
                return;
            if (state.pumps === 0) {
                // Encaisser à 0 pump est autorisé mais résout 0 banked.
            }
            settle(state.stim, 'banked');
        },
        setLevel(newLevel) {
            const v = variantForLevel(newLevel);
            update({ level: newLevel, variant: v });
        },
    };
}
//# sourceMappingURL=adapter.js.map